import { type BaseBullet } from "@common/utils/baseBullet";
import { type Hitbox, HitboxType } from "@common/utils/hitbox";

/*
    What the bullets in flight could strike, gathered once a frame (0.98.8, the owner: "find whatever blocks, whatever
    is redundant, and remove it"): every bullet tested every object the page knows, every frame, with a full line test
    (a building's every wall, through vectors made and thrown away), a firefight's dozens of bullets by hundreds of
    things. Now each frame keeps the objects a bullet tests at all (damageable and standing, nothing a bullet passes
    over), in the pool's order, so a frame's hits come out the same and in the same order; an obstacle's or a
    building's hitbox bounds are kept with it, and a bullet tests only those whose bounds its step crosses (a unit wider
    each way: a line can't cross a hitbox where its bounds aren't); the rest (a player: their melee's reflecting surface
    reaches past them; an animal, a grenade) it tests as before.
*/

/** what a bullet tests (common/src/utils/baseBullet.ts takes any iterable of them) */
type Target = Parameters<BaseBullet["updateAndGetCollisions"]>[1] extends Iterable<infer T> ? T : never;

/** a bullet's step widened by this much each way (game units) before its bounds are compared: well past a line test's own nudge */
const MARGIN = 1;

interface Entry {
    /** (none past the frame's last: no object kept longer than its frame) */
    object?: Target
    /** an obstacle or a building, tested by its hitbox's bounds first */
    bounded: boolean
    minX: number
    minY: number
    maxX: number
    maxY: number
}

/** a hitbox's bounds added into `entry` (none made: the hitbox's own numbers read) */
function grow(entry: Entry, hitbox: Hitbox): void {
    switch (hitbox.type) {
        case HitboxType.Rect:
            entry.minX = Math.min(entry.minX, hitbox.min.x); entry.minY = Math.min(entry.minY, hitbox.min.y);
            entry.maxX = Math.max(entry.maxX, hitbox.max.x); entry.maxY = Math.max(entry.maxY, hitbox.max.y);
            break;
        case HitboxType.Circle:
            entry.minX = Math.min(entry.minX, hitbox.position.x - hitbox.radius); entry.minY = Math.min(entry.minY, hitbox.position.y - hitbox.radius);
            entry.maxX = Math.max(entry.maxX, hitbox.position.x + hitbox.radius); entry.maxY = Math.max(entry.maxY, hitbox.position.y + hitbox.radius);
            break;
        case HitboxType.Group:
            for (const part of hitbox.hitboxes) grow(entry, part);
            break;
        case HitboxType.Polygon:
            for (const point of hitbox.points) {
                entry.minX = Math.min(entry.minX, point.x); entry.minY = Math.min(entry.minY, point.y);
                entry.maxX = Math.max(entry.maxX, point.x); entry.maxY = Math.max(entry.maxY, point.y);
            }
            break;
    }
}

class BulletTargetsClass {
    private readonly _entries: Entry[] = [];
    private _used = 0;
    private readonly _near: Target[] = [];

    /** the frame's objects gathered, before the bullets move (game.ts) */
    gather(objects: Iterable<Target>): void {
        let used = 0;
        for (const object of objects) {
            // (what every bullet skips anyway: nothing that can't be hit, nothing broken, nothing bullets pass over)
            if (!object.damageable || object.dead) continue;
            const isBuilding = object.isBuilding === true, isObstacle = object.isObstacle === true;
            const bounded = isObstacle || isBuilding;
            let hitbox: Hitbox | undefined;
            if (bounded) {
                if ((object as { definition: { noBulletCollision?: boolean } }).definition.noBulletCollision) continue;
                hitbox = (object as { hitbox?: Hitbox }).hitbox;
                // (no hitbox: no line test ever strikes it)
                if (!hitbox) continue;
            }
            let entry = this._entries[used];
            if (!entry) this._entries[used] = entry = { object, bounded, minX: 0, minY: 0, maxX: 0, maxY: 0 };
            else {
                entry.object = object;
                entry.bounded = bounded;
            }
            used++;
            if (hitbox) {
                entry.minX = entry.minY = Infinity;
                entry.maxX = entry.maxY = -Infinity;
                grow(entry, hitbox);
            }
        }
        // (the rest let go: no object kept past its frame)
        for (let i = used; i < this._used; i++) this._entries[i].object = undefined;
        this._used = used;
    }

    /**
     * what `bullet` could strike this frame, stepping `delta` ms (its step from where it is, along its velocity: its
     * range cuts it short, never longer), in the pool's order. The list is the same one each call, filled afresh: a
     * bullet's test reads it through before the next bullet's
     */
    near(bullet: BaseBullet, delta: number): readonly Target[] {
        const x0 = bullet.position.x, y0 = bullet.position.y;
        const x1 = x0 + bullet.velocity.x * delta, y1 = y0 + bullet.velocity.y * delta;
        const minX = Math.min(x0, x1) - MARGIN, minY = Math.min(y0, y1) - MARGIN;
        const maxX = Math.max(x0, x1) + MARGIN, maxY = Math.max(y0, y1) + MARGIN;
        const out = this._near;
        out.length = 0;
        for (let i = 0; i < this._used; i++) {
            const entry = this._entries[i];
            if (!entry.object || (entry.bounded && (entry.maxX < minX || entry.minX > maxX || entry.maxY < minY || entry.minY > maxY))) continue;
            out.push(entry.object);
        }
        return out;
    }
}

export const BulletTargets = new BulletTargetsClass();
